import { useCallback, useEffect, useRef, useState } from "react";
import type { SchemaRegion } from "../../api/restaurantSchema";
import { REGION_DIMENSIONS, contentBounds, type LogicalBounds } from "./geometry";
import type { LogicalPoint } from "./stageLayout";

export const MIN_ZOOM = 0.5;
export const MAX_ZOOM = 2.5;
export const ZOOM_STEP = 0.25;
// Отстъп около вписаното съдържание (пиксели): отгоре/встрани 40 като в
// дизайна; отдолу същото, а с `roomForToolbar` — 96, за да плава лентата за
// добавяне там.
export const STAGE_PADDING_X = 40;
export const STAGE_PADDING_TOP = 40;
// С плаващо поле горе в платното („Маркиране" → Схема: „Сервитьор") — още
// място отгоре, за да не покрива то чипа над горната маса.
const STAGE_PADDING_TOP_CONTROL = 72;
const STAGE_PADDING_BOTTOM = 40;
const STAGE_PADDING_BOTTOM_TOOLBAR = 96;
// От тази ширина на платното редакторът събира лентата и зуума на един ред долу.
const WIDE_CANVAS_PX = 1040;
// Най-малката област, която „Побери" вписва (логически единици) — една-две
// маси не се раздуват през цялото платно.
const MIN_FIT_WIDTH = 900;
const MIN_FIT_HEIGHT = 600;
// Колелцето с Ctrl/⌘ (и щипането на тъчпада) в редактора: колко се сменя зуумът на пиксел.
const WHEEL_ZOOM_SPEED = 0.0025;

/** Изгледът: пиксел в платното = offset + логическа × scale. */
interface View {
  scale: number;
  offsetX: number;
  offsetY: number;
}

// Какво вписва „Побери": всички елементи; празен регион — самият регион.
const fitBoundsOf = (region: SchemaRegion | undefined): LogicalBounds => {
  const bounds = region ? contentBounds(region.elements) : null;
  if (bounds) return bounds;
  const { width, height } = REGION_DIMENSIONS[region?.shape ?? "rectangle"];
  return { left: 0, top: 0, right: width, bottom: height };
};

// Разширява рамката до MIN_FIT_* около центъра ѝ.
const withMinimumSize = (bounds: LogicalBounds): LogicalBounds => {
  const width = Math.max(MIN_FIT_WIDTH, bounds.right - bounds.left);
  const height = Math.max(MIN_FIT_HEIGHT, bounds.bottom - bounds.top);
  const centerX = (bounds.left + bounds.right) / 2;
  const centerY = (bounds.top + bounds.bottom) / 2;
  return {
    left: centerX - width / 2,
    top: centerY - height / 2,
    right: centerX + width / 2,
    bottom: centerY + height / 2,
  };
};

/**
 * Изгледът на сцената — безкрайно платно: елементите стоят навсякъде, а
 * платното се мести (влачене по празното място, колелце/тъчпад) и зуумва
 * (бутоните, Ctrl/⌘ + колелце). „Побери схемата" („100%") вписва ВСИЧКИ
 * елементи максимално близо, без да крие нито един. Докато потребителят не
 * е пипал изгледа, той се вписва наново при всяка промяна на размера; друг
 * регион → отново вписан. Hook-ът е у потребителя (редактор, резервации), за
 * да знае `scale` и да сложи зуума където иска; резултатът се подава на
 * `<SchemaStage stage={…} />`.
 */
export const useSchemaStage = ({
  region,
  roomForToolbar = false,
  roomForTopControl = false,
  alwaysPannable = false,
}: {
  region: SchemaRegion | undefined;
  // Редакторът: повече място отдолу за плаващата лента за добавяне.
  roomForToolbar?: boolean;
  // Плаващо поле горе в платното — повече място отгоре.
  roomForTopControl?: boolean;
  /**
   * Редакторът: платното се мести винаги, а Ctrl/⌘ + колелце зуумва под
   * курсора. Иначе (резервации) — мести се само след зуум с бутоните, а
   * вписаната схема оставя колелцето, пръста и Ctrl/⌘ + колелце на страницата.
   */
  alwaysPannable?: boolean;
}) => {
  // Callback ref — платното може да се появи по-късно (регионът още се зарежда).
  const [viewport, setViewport] = useState<HTMLDivElement | null>(null);
  const [viewportSize, setViewportSize] = useState({ width: 0, height: 0 });
  const paddingBottom = roomForToolbar ? STAGE_PADDING_BOTTOM_TOOLBAR : STAGE_PADDING_BOTTOM;
  const paddingTop = roomForTopControl ? STAGE_PADDING_TOP_CONTROL : STAGE_PADDING_TOP;
  const regionId = region?.id ?? null;

  // Рамката за вписване се снима при смяна на региона и при „Побери" — не при
  // всяко местене (иначе изгледът би скачал под ръката). Ключът е регионът:
  // друг регион → нова рамка още в същия рендер, без премигване.
  const [fitState, setFitState] = useState<{ regionId: number | null; bounds: LogicalBounds } | null>(null);
  const isFitCurrent = fitState !== null && fitState.regionId === regionId;
  const snapshotBounds = isFitCurrent ? fitState.bounds : fitBoundsOf(region);
  // Производно състояние (setState по време на рендер — React го позволява).
  if (!isFitCurrent) setFitState({ regionId, bounds: snapshotBounds });
  const fitBounds = withMinimumSize(snapshotBounds);
  // Ръчният изглед (след зуум/местене); null — вписан.
  const [manualState, setManualState] = useState<{ regionId: number | null; view: View } | null>(null);
  const manualView = manualState && manualState.regionId === regionId ? manualState.view : null;

  useEffect(() => {
    if (!viewport) return;
    const observer = new ResizeObserver(([entry]) => {
      setViewportSize({
        width: entry.contentRect.width,
        height: entry.contentRect.height,
      });
    });
    observer.observe(viewport);
    return () => observer.disconnect();
  }, [viewport]);

  const fitWidth = fitBounds.right - fitBounds.left;
  const fitHeightLogical = fitBounds.bottom - fitBounds.top;
  const availableWidth = viewportSize.width - STAGE_PADDING_X * 2;
  const availableHeight = viewportSize.height - paddingTop - paddingBottom;
  const fitScale = Math.max(0.02, Math.min(availableWidth / fitWidth, availableHeight / fitHeightLogical));
  const fittedView: View = {
    scale: fitScale,
    offsetX: STAGE_PADDING_X + (availableWidth - fitWidth * fitScale) / 2 - fitBounds.left * fitScale,
    offsetY: paddingTop + (availableHeight - fitHeightLogical * fitScale) / 2 - fitBounds.top * fitScale,
  };
  const view = manualView ?? fittedView;
  const zoom = view.scale / fitScale;
  const canPan = alwaysPannable || manualView !== null;

  // Височината, при която вписаното съдържание се събира точно по ширината на
  // платното (без празно отгоре/отдолу) — за платно, високо колкото схемата.
  // null — ширината още не е измерена.
  const fitHeight =
    viewportSize.width > 0
      ? Math.ceil((availableWidth / fitWidth) * fitHeightLogical + paddingTop + paddingBottom)
      : null;

  // Слушателите на колелцето живеят извън React — четат последния изглед оттук.
  const latest = useRef({ view, fitScale, canPan, regionId, alwaysPannable });
  latest.current = { view, fitScale, canPan, regionId, alwaysPannable };

  const setView = useCallback((next: View) => {
    setManualState({ regionId: latest.current.regionId, view: next });
  }, []);

  /** Зуум с котва в точка от платното (пиксели спрямо горния му ляв ъгъл). */
  const zoomAround = useCallback(
    (nextZoom: number, anchorX: number, anchorY: number) => {
      const { view: current, fitScale: currentFit } = latest.current;
      const clamped = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, nextZoom));
      const scale = clamped * currentFit;
      const logicalX = (anchorX - current.offsetX) / current.scale;
      const logicalY = (anchorY - current.offsetY) / current.scale;
      setView({ scale, offsetX: anchorX - logicalX * scale, offsetY: anchorY - logicalY * scale });
    },
    [setView],
  );

  const panBy = useCallback(
    (deltaX: number, deltaY: number) => {
      const { view: current } = latest.current;
      setView({ ...current, offsetX: current.offsetX + deltaX, offsetY: current.offsetY + deltaY });
    },
    [setView],
  );

  // Колелце/тъчпад: в редактора с Ctrl/⌘ (и щипане) — зуум под курсора;
  // иначе — местене, когато платното се мести (Ctrl/⌘ извън редактора остава на браузъра).
  useEffect(() => {
    if (!viewport) return;
    const handleWheel = (event: WheelEvent) => {
      const { view: current, fitScale: currentFit, canPan: pannable, alwaysPannable: isEditor } = latest.current;
      const withModifier = event.ctrlKey || event.metaKey;
      const isZoom = isEditor && withModifier;
      if (!isZoom && (!pannable || withModifier)) return;
      event.preventDefault();
      if (isZoom) {
        const rect = viewport.getBoundingClientRect();
        const factor = Math.exp(-event.deltaY * WHEEL_ZOOM_SPEED);
        zoomAround((current.scale / currentFit) * factor, event.clientX - rect.left, event.clientY - rect.top);
      } else {
        panBy(-event.deltaX, -event.deltaY);
      }
    };
    viewport.addEventListener("wheel", handleWheel, { passive: false });
    return () => viewport.removeEventListener("wheel", handleWheel);
  }, [viewport, zoomAround, panBy]);

  const changeZoom = useCallback(
    (delta: number) => {
      const { view: current, fitScale: currentFit } = latest.current;
      // Стъпките са кръгли (25%, 50%…) и след зуум с колелцето.
      const steps = Math.round(current.scale / currentFit / ZOOM_STEP) + Math.sign(delta);
      zoomAround(steps * ZOOM_STEP, viewportSize.width / 2, viewportSize.height / 2);
    },
    [zoomAround, viewportSize],
  );

  // „Побери": снима наново рамката на всички елементи и вписва.
  const fit = useCallback(() => {
    setFitState({ regionId, bounds: fitBoundsOf(region) });
    setManualState(null);
  }, [region, regionId]);

  /**
   * Точка на екрана → логическа позиция; null извън платното. Няма граница —
   * всяка точка върху платното е валидна позиция.
   */
  const clientToLogical = useCallback(
    (clientX: number, clientY: number): LogicalPoint | null => {
      if (!viewport) return null;
      const canvas = viewport.getBoundingClientRect();
      const isOnCanvas =
        clientX >= canvas.left && clientX <= canvas.right && clientY >= canvas.top && clientY <= canvas.bottom;
      if (!isOnCanvas) return null;
      return {
        x: (clientX - canvas.left - view.offsetX) / view.scale,
        y: (clientY - canvas.top - view.offsetY) / view.scale,
      };
    },
    [view, viewport],
  );

  /** Средата на видимото платно (над лентата за добавяне) в логически единици. */
  const viewCenter = useCallback(
    (): LogicalPoint => ({
      x: (viewportSize.width / 2 - view.offsetX) / view.scale,
      y: ((viewportSize.height - paddingBottom + paddingTop) / 2 - view.offsetY) / view.scale,
    }),
    [view, viewportSize, paddingBottom, paddingTop],
  );

  return {
    viewportRef: setViewport,
    viewportSize,
    fitScale,
    fitHeight,
    // Къде е логическата точка (0, 0) в платното (пиксели) — от нея тръгва и карето на пода.
    origin: { left: view.offsetX, top: view.offsetY },
    scale: view.scale,
    zoom,
    canPan,
    panBy,
    roomForToolbar,
    isWide: viewportSize.width >= WIDE_CANVAS_PX,
    clientToLogical,
    viewCenter,
    // Готово за `<ZoomControls placement=… {...stage.zoomControls} />`.
    // Допускът — зуумът с колелцето не е точно кратно на стъпката.
    zoomControls: {
      zoom,
      canZoomOut: zoom > MIN_ZOOM + 0.001,
      canZoomIn: zoom < MAX_ZOOM - 0.001,
      onZoomOut: () => changeZoom(-1),
      onZoomIn: () => changeZoom(1),
      onFit: fit,
    },
  };
};

export type SchemaStageState = ReturnType<typeof useSchemaStage>;
